import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Hotel, Briefcase, Ticket, Plane, Check,
  ShieldCheck, Users, DollarSign, Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IMG = {
  heroYacht: "https://images.unsplash.com/photo-1598737285721-29346a5c9278?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxzdXBlcnlhY2h0JTIwb2NlYW58ZW58MHx8fHwxNzg1OTE5MDI3fDA&ixlib=rb-4.1.0&q=85",
  hotel: "https://images.pexels.com/photos/34496715/pexels-photo-34496715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  corporate: "https://images.unsplash.com/photo-1661954864180-e61dea14208a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwamV0JTIwaW50ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzg1OTE5MDI3fDA&ixlib=rb-4.1.0&q=85",
  f1: "https://images.unsplash.com/photo-1752884991461-8ac432ad9266?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxmb3JtdWxhJTIwMSUyMHJhY2luZyUyMHRyYWNrfGVufDB8fHx8MTc4NTkxOTAyOHww&ixlib=rb-4.1.0&q=85",
  jet: "https://images.pexels.com/photos/14914173/pexels-photo-14914173.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  concierge: "https://images.unsplash.com/photo-1775257796019-3e8db981a1a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwY29uY2llcmdlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg1OTE5MDQwfDA&ixlib=rb-4.1.0&q=85",
  villa1: "https://images.unsplash.com/photo-1521750465-672a0f580901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBwcml2YXRlJTIwdmlsbGF8ZW58MHx8fHwxNzg1OTE5MTI3fDA&ixlib=rb-4.1.0&q=85",
  suite: "https://images.unsplash.com/photo-1609602126247-4ab7188b4aa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBzdWl0ZXxlbnwwfHx8fDE3ODU5MTkxMjd8MA&ixlib=rb-4.1.0&q=85",
  dining: "https://images.unsplash.com/photo-1773855742951-10cd0b8aff6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZmluZSUyMGRpbmluZ3xlbnwwfHx8fDE3ODU5MTkxMjd8MA&ixlib=rb-4.1.0&q=85",
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      variants={fadeUp}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
    >
      {children}
    </motion.div>
  );
}

const SERVICES = [
  { icon: Hotel, n: "01", title: "Hoteles & Villas de Lujo", desc: "Tarifas privadas y suites exclusivas en propiedades cinco estrellas y villas de todo el mundo — incluidas habitaciones que el público jamás podría reservar. Reserva de propiedades al completo." },
  { icon: Briefcase, n: "02", title: "Viajes Corporativos", desc: "Negociación de tarifas, logística de grupos y coordinación de congresos para equipos directivos y eventos. Impecable, puntual, dentro del presupuesto." },
  { icon: Plane, n: "03", title: "Aviación & Yates", desc: "Gulfstreams listos para despegar en horas. Superyates a disposición en el Mediterráneo. Orquestado de principio a fin." },
  { icon: Sparkles, n: "04", title: "Experiencias & Celebraciones", desc: "Con todo lujo de detalles: reservas de restaurantes para celebraciones y reuniones de familia y empresa, visitas exclusivas de viñedos, entradas a eventos y experiencias a medida." },
];

const STATS = [
  { v: "24/7", l: "Concierge Global" },
  { v: "60+", l: "Destinos" },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div className="bg-[#050505] text-white overflow-hidden">
      <Navbar />
      <Hero heroRef={heroRef} y={y} heroOpacity={heroOpacity} />
      <Services />
      <Manifesto />
      <Membership />
      <Experiences />
      <Partners />
      <Apply />
      <Footer />
    </div>
  );
}

function Hero({ heroRef, y, heroOpacity }) {
  return (
    <section ref={heroRef} className="relative h-screen min-h-[720px] flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={IMG.heroYacht} alt="Superyate en el mar" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
      </motion.div>

      <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 w-full">
        <Reveal>
          <p className="text-xs tracking-luxe uppercase text-gold mb-8">Un Concierge de Viajes Privado</p>
        </Reveal>
        <Reveal i={1}>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl">
            El mundo, <span className="italic text-gold">sin fricción.</span>
          </h1>
        </Reveal>
        <Reveal i={2}>
          <p className="text-white/70 font-light text-lg max-w-xl mt-8 leading-relaxed">
            Para quienes su tiempo es su moneda más valiosa. Orquestamos acceso,
            discreción y lo extraordinario — para que usted, simplemente, llegue.
          </p>
        </Reveal>
        <Reveal i={3}>
          <div className="flex flex-wrap gap-5 mt-12">
            <button
              data-testid="hero-membership-btn"
              onClick={() => document.querySelector("#membership")?.scrollIntoView({ behavior: "smooth" })}
              className="group bg-gold text-black px-9 py-4 text-xs tracking-luxe-sm uppercase flex items-center gap-3 hover:bg-[#C5A059] transition-colors hover:scale-[1.02]"
            >
              Alta Nuevo Socio
              <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              className="border border-white/25 px-9 py-4 text-xs tracking-luxe-sm uppercase hover:border-gold hover:text-gold transition-colors"
            >
              Descubrir Servicios
            </button>
          </div>
        </Reveal>
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="py-32 border-b border-white/10" data-testid="manifesto-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-xs tracking-luxe uppercase text-gold mb-10">Lo Que Realmente Vendemos</p>
        </Reveal>
        <Reveal i={1}>
          <h2 className="font-cormorant text-3xl md:text-5xl leading-tight text-white/90 font-light">
            No estamos en el negocio de reservar viajes. Comerciamos con
            <span className="text-gold"> tiempo</span>,
            <span className="text-gold"> acceso</span> y
            <span className="text-gold"> tranquilidad</span> — las tres cosas
            que las personas más exigentes del mundo no pueden comprar en una estantería.
          </h2>
        </Reveal>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="pt-40 pb-32 bg-[#050505] relative z-10" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-6">
          <div>
            <Reveal><p className="text-xs tracking-luxe uppercase text-gold mb-6">Nuestro Mundo</p></Reveal>
            <Reveal i={1}>
              <h2 className="font-serif-display text-4xl md:text-6xl max-w-2xl leading-tight">
                Diseñado para lo excepcional
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} i={i}>
              <div
                data-testid={`service-card-${s.n}`}
                className="group border border-white/10 p-10 lg:p-12 hover:border-gold/40 transition-colors duration-500 h-full"
              >
                <div className="flex items-start justify-between mb-16">
                  <s.icon size={34} strokeWidth={1} className="text-gold" />
                  <span className="font-serif-display text-white/20 text-3xl">{s.n}</span>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl mb-4 group-hover:text-gold transition-colors">
                  {s.title}
                </h3>
                <p className="text-white/55 font-light leading-relaxed max-w-md">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Membership() {
  const [plans, setPlans] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const checkout = async (lookup_key) => {
    if (!user) {
      toast.info("Cree su cuenta para activar Concierge+");
      navigate("/register?plan=" + lookup_key);
      return;
    }
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  return (
    <section id="membership" className="py-32 relative border-t border-white/10" data-testid="membership-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <Reveal><p className="text-xs tracking-luxe uppercase text-gold mb-6">Ser Socio Concierge+</p></Reveal>
        <Reveal i={1}>
          <h2 className="font-serif-display text-4xl md:text-6xl max-w-2xl leading-tight mb-6">
            Su llave privada a una vida sin fricción
          </h2>
        </Reveal>
        <Reveal i={2}>
          <p className="text-white/60 font-light max-w-xl mb-16 leading-relaxed">
            Un concierge dedicado. Acceso ilimitado a las personas,
            lugares y experiencias reservadas para unos pocos.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
          {plans.map((p, i) => (
            <Reveal key={p.id} i={i}>
              <div
                data-testid={`plan-card-${p.lookup_key}`}
                className={`p-10 lg:p-12 h-full flex flex-col backdrop-blur-sm ${
                  p.interval === "month"
                    ? "border-2 border-gold bg-black/40"
                    : "border border-white/15 bg-black/30"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-serif-display text-2xl">{p.name}</h3>
                  {p.interval === "month" && (
                    <span className="text-[10px] tracking-luxe-sm uppercase bg-gold text-black px-3 py-1">Signature</span>
                  )}
                </div>
                <p className="text-white/50 font-light text-sm mb-8">{p.tagline}</p>
                <div className="flex items-baseline gap-2 mb-10">
                  <span className="font-serif-display text-5xl text-gold">
                    {p.price.toLocaleString("es-ES")} €
                  </span>
                  <span className="text-white/50 font-light">/ {p.interval === "month" ? "mes" : "año"}</span>
                </div>
                <ul className="space-y-4 mb-10 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-white/70 font-light text-sm">
                      <Check size={16} strokeWidth={1.5} className="text-gold mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-checkout-${p.lookup_key}`}
                  onClick={() => { window.location.href = p.payment_link; }}
                  className={`w-full py-4 text-xs tracking-luxe-sm uppercase transition-colors disabled:opacity-50 ${
                    p.interval === "month"
                      ? "bg-gold text-black hover:bg-[#C5A059]"
                      : "border border-white/25 hover:border-gold hover:text-gold"
                  }`}
                >
                  Ser Socio
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experiences() {
  const tiles = [
    { img: IMG.villa1, label: "Villas Privadas", span: "md:col-span-2 h-[320px]" },
    { img: IMG.suite, label: "Suites Signature", span: "md:col-span-2 h-[320px]" },
    { img: IMG.jet, label: "Aviación Privada", span: "md:col-span-2 h-[320px]" },
    { img: IMG.dining, label: "Mesas Privadas", span: "md:col-span-2 h-[320px]" },
  ];
  return (
    <section id="experiences" className="py-32 border-t border-white/10" data-testid="experiences-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal><p className="text-xs tracking-luxe uppercase text-gold mb-6">Un Vistazo</p></Reveal>
        <Reveal i={1}>
          <h2 className="font-serif-display text-4xl md:text-6xl mb-16 max-w-2xl leading-tight">
            Si puede soñarlo, lo hacemos realidad
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-4 gap-4 auto-rows-min">
          {tiles.map((t, i) => (
            <Reveal key={t.label} i={i} className={t.span}>
              <div className="hover-zoom relative overflow-hidden h-full group cursor-pointer">
                <img src={t.img} alt={t.label} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <span className="absolute bottom-6 left-6 font-serif-display text-xl group-hover:text-gold transition-colors">
                  {t.label}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  const navigate = useNavigate();
  const points = [
    { icon: Users, t: "Solo presente", d: "Usted no vende viajes. Presenta a un cliente — nosotros hacemos el resto, con discreción." },
    { icon: DollarSign, t: "Gane al confirmar", d: "Reciba una comisión generosa en el momento en que se confirma una reserva." },
    { icon: ShieldCheck, t: "Red de élite", d: "Únase a nuestra red de abogados, directivos y family offices que ofrecen el mejor servicio a sus clientes con nosotros." },
  ];
  return (
    <section id="partners" className="py-32 border-t border-white/10 relative" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <Reveal><p className="text-xs tracking-luxe uppercase text-gold mb-6">Programa de Partners</p></Reveal>
            <Reveal i={1}>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-tight mb-8">
                Convierta su agenda de contactos en ingresos
              </h2>
            </Reveal>
            <Reveal i={2}>
              <p className="text-white/60 font-light mb-12 leading-relaxed max-w-lg">
                Nuestro canal más poderoso. Una red selecta de personas bien conectadas que
                presentan clientes y ganan cada vez que se confirma una reserva — sin vender nada.
              </p>
            </Reveal>
            <div className="space-y-8 mb-12">
              {points.map((p, i) => (
                <Reveal key={p.t} i={i}>
                  <div className="flex gap-5">
                    <p.icon size={26} strokeWidth={1} className="text-gold shrink-0 mt-1" />
                    <div>
                      <h4 className="font-serif-display text-xl mb-1">{p.t}</h4>
                      <p className="text-white/55 font-light text-sm max-w-md">{p.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal i={3}>
              <button
                data-testid="become-partner-btn"
                onClick={() => navigate("/register?role=partner")}
                className="group bg-gold text-black px-9 py-4 text-xs tracking-luxe-sm uppercase flex items-center gap-3 hover:bg-[#C5A059] transition-colors hover:scale-[1.02]"
              >
                Ser Partner
                <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </Reveal>
          </div>

          <Reveal i={1}>
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((s) => (
                <div key={s.l} className="border border-white/10 p-10 hover:border-gold/40 transition-colors" data-testid={`stat-${s.l}`}>
                  <p className="font-serif-display text-4xl md:text-5xl text-gold mb-3">{s.v}</p>
                  <p className="text-white/50 font-light text-xs tracking-luxe-sm uppercase">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Apply() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", interest: "membership", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/applications", form);
      toast.success(data.message);
      setForm({ full_name: "", email: "", phone: "", interest: "membership", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/20 py-4 text-white placeholder-white/30 font-light focus:border-gold focus:outline-none transition-colors";

  return (
    <section id="apply" className="py-32 border-t border-white/10" data-testid="apply-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <Reveal><p className="text-xs tracking-luxe uppercase text-gold mb-6 text-center">Solicite una Presentación</p></Reveal>
        <Reveal i={1}>
          <h2 className="font-serif-display text-4xl md:text-6xl text-center mb-4 leading-tight">Comencemos, con discreción</h2>
        </Reveal>
        <Reveal i={2}>
          <p className="text-white/55 font-light text-center mb-16 max-w-lg mx-auto">
            El alta de socio es por solicitud. Comparta unos datos y un miembro de nuestro equipo le contactará de forma privada.
          </p>
        </Reveal>
        <Reveal i={3}>
          <form onSubmit={submit} className="space-y-8" data-testid="apply-form">
            <div className="grid md:grid-cols-2 gap-8">
              <input required data-testid="apply-name" className={field} placeholder="Nombre completo"
                value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              <input required type="email" data-testid="apply-email" className={field} placeholder="Email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <input data-testid="apply-phone" className={field} placeholder="Teléfono (opcional)"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <select data-testid="apply-interest" className={`${field} [&>option]:bg-[#121212]`}
                value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}>
                <option value="membership">Ser Socio Concierge+</option>
                <option value="corporate">Viajes Corporativos</option>
                <option value="experience">Experiencias VIP</option>
                <option value="aviation">Aviación & Yates</option>
                <option value="partner">Programa de Partners</option>
              </select>
            </div>
            <textarea rows={3} data-testid="apply-message" className={field} placeholder="¿Cómo podemos ayudarle? (opcional)"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button type="submit" disabled={sending} data-testid="apply-submit"
              className="w-full bg-gold text-black py-5 text-xs tracking-luxe uppercase hover:bg-[#C5A059] transition-colors disabled:opacity-50">
              {sending ? "Enviando…" : "Enviar Solicitud"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
